import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/services/adminApi';

/**
 * Admin User Management Page
 * 관리자 사용자 관리 페이지
 */
const UserManagementPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'NORMAL',
    name: '',
    email: '',
  });
  const queryClient = useQueryClient();

  const { data: usersData, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminApi.getAdminUsers(),
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => adminApi.createAdminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setOpenDialog(false);
      setFormData({ username: '', password: '', role: 'NORMAL', name: '', email: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateAdminUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setOpenDialog(false);
      setEditingUser(null);
      setFormData({ username: '', password: '', role: 'NORMAL', name: '', email: '' });
    },
    onError: (error: any) => {
      console.error('Failed to update admin user:', error);
      const errorMessage = error?.response?.data?.message || error?.message || '사용자 수정에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteAdminUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const users = (usersData as any)?.data?.data || usersData?.data || [];

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'NORMAL', name: '', email: '' });
    setOpenDialog(true);
  };

  const handleOpenEdit = (user: any) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      name: user.name || '',
      email: user.email || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ username: '', password: '', role: 'NORMAL', name: '', email: '' });
  };

  const handleSubmit = () => {
    // 수정 모드일 때 비밀번호가 비어있거나 공백만 있으면 제거
    if (editingUser) {
      const submitData: any = { ...formData };
      // username은 수정할 수 없으므로 제거
      delete submitData.username;
      // 비밀번호가 비어있거나 공백만 있으면 제거
      if (!submitData.password || submitData.password.trim() === '') {
        delete submitData.password;
      } else {
        // 비밀번호가 8자 미만이면 경고
        if (submitData.password.trim().length < 8) {
          alert('비밀번호는 최소 8자 이상이어야 합니다.');
          return;
        }
        // 공백 제거된 비밀번호로 업데이트
        submitData.password = submitData.password.trim();
      }
      updateMutation.mutate({ id: editingUser.id, data: submitData });
    } else {
      // 생성 모드일 때는 비밀번호 필수
      if (!formData.password || formData.password.trim() === '') {
        alert('비밀번호를 입력해주세요.');
        return;
      }
      if (formData.password.trim().length < 8) {
        alert('비밀번호는 최소 8자 이상이어야 합니다.');
        return;
      }
      const submitData: any = { ...formData, password: formData.password.trim() };
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('이 관리자 사용자를 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">관리자 사용자 관리</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          사용자 추가
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>사용자명</TableCell>
              <TableCell>역할</TableCell>
              <TableCell>이름</TableCell>
              <TableCell>이메일</TableCell>
              <TableCell>활성 상태</TableCell>
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  로딩 중...
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  사용자가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.name || '-'}</TableCell>
                  <TableCell>{user.email || '-'}</TableCell>
                  <TableCell>{user.isActive ? '활성' : '비활성'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(user)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(user.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? '사용자 수정' : '사용자 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="사용자명"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={!!editingUser}
              required
            />
            <TextField
              label="비밀번호"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
              helperText={editingUser ? '비밀번호를 변경하지 않으려면 비워두세요' : ''}
            />
            <FormControl fullWidth>
              <InputLabel>역할</InputLabel>
              <Select
                value={formData.role}
                label="역할"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="SUPER">슈퍼 관리자</MenuItem>
                <MenuItem value="NORMAL">일반 관리자</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="이름"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
